import { TUNABLES } from "../config";
import { count, isRecord, jsonRecord, MAX_SESSION_CHARS, oneOf, text } from "./protocol";

/**
 * AP1, what an account has earned, kept with the account on the Worker (`/points`, the owner's
 * ask of 2026-10-21: "the points will be attached to the user that is tied to the google"): the
 * purse, the renown and the runs started, the three counts of the save's bank. Shared by the page
 * (`src/front/accountPoints.ts`) and the Worker (`server/points.ts`).
 *
 * A page shows its session, its bank as the save holds it, and the bank it last agreed with the
 * Worker (`base`, with the row's version; none on this browser's first sync for the account).
 * The Worker answers the bank to keep and its new version: a three-way merge. Where only one side
 * moved since the agreement it wins, so coins spent stay spent; where both moved, or at the
 * first sync, each count is the larger of the two (the owner's rule, 2026-10-21).
 */
export interface AccountPoints {
  readonly purse: number;
  readonly renown: number;
  readonly runs: number;
}

export interface AgreedPoints {
  readonly version: number;
  readonly points: AccountPoints;
}

export interface PointsRequest {
  readonly session: string;
  readonly points: AccountPoints;
  readonly base: AgreedPoints | null;
}

/** Why a sync was turned down: the session, the request's shape, the switch off, no key. */
export const POINTS_REFUSALS = ["session", "shape", "off", "setup"] as const;
export type PointsRefusal = (typeof POINTS_REFUSALS)[number];

export type PointsAnswer = AgreedPoints | { readonly refused: PointsRefusal };

/** The three counts, each a whole number from 0 to `maxCount`; undefined for anything else. */
export function pointsOf(value: unknown): AccountPoints | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const purse = count(value.purse, TUNABLES.points.maxCount);
  const renown = count(value.renown, TUNABLES.points.maxCount);
  const runs = count(value.runs, TUNABLES.points.maxCount);
  return purse === undefined || renown === undefined || runs === undefined
    ? undefined
    : { purse, renown, runs };
}

function agreedOf(value: unknown): AgreedPoints | undefined {
  if (!isRecord(value)) {
    return undefined;
  }
  const version = count(value.version);
  const points = pointsOf(value.points);
  return version === undefined || points === undefined ? undefined : { version, points };
}

/** A request from a page, checked; undefined when it is not one. */
export function parsePointsRequest(raw: string): PointsRequest | undefined {
  if (raw.length > TUNABLES.points.maxRequestChars) {
    return undefined;
  }
  const value = jsonRecord(raw);
  const session = text(value?.session, MAX_SESSION_CHARS);
  const points = pointsOf(value?.points);
  const base = value?.base === null ? null : agreedOf(value?.base);
  return session && points && base !== undefined ? { session, points, base } : undefined;
}

/** The Worker's answer, checked; undefined for anything else (an older Worker, a proxy's page). */
export function parsePointsAnswer(raw: string): PointsAnswer | undefined {
  const value = jsonRecord(raw);
  const agreed = agreedOf(value);
  if (agreed) {
    return agreed;
  }
  const refused = oneOf(POINTS_REFUSALS, value?.refused);
  return refused ? { refused } : undefined;
}

/** The points endpoint beside a lobby server: `wss://host` becomes `https://host/points`. */
export function pointsUrlOf(lobbyUrl: string): string {
  return `${lobbyUrl.replace(/^ws(s?):\/\//, "http$1://")}/points`;
}

export function samePoints(first: AccountPoints, second: AccountPoints): boolean {
  return (
    first.purse === second.purse && first.renown === second.renown && first.runs === second.runs
  );
}

/** Each count the larger of the two: the merge where both sides moved (the owner's rule). */
export function largerOf(first: AccountPoints, second: AccountPoints): AccountPoints {
  return {
    purse: Math.max(first.purse, second.purse),
    renown: Math.max(first.renown, second.renown),
    runs: Math.max(first.runs, second.runs),
  };
}
